// src/admin/components/StatCard.jsx
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

export default function StatCard({
  title,
  value,
  icon: Icon,     // <-- treat icon prop as a Component
  to,
  onClick,
  hint,
  accent = false,
}) {
  const navigate = useNavigate();
  const [bump, setBump] = useState(false);
  const prevValueRef = useRef(value);

  const clickable = Boolean(to || onClick);

  useEffect(() => {
    const prev = prevValueRef.current;
    if (prev !== value) {
      setBump(true);
      const t = setTimeout(() => setBump(false), 260);
      prevValueRef.current = value;
      return () => clearTimeout(t);
    }
    prevValueRef.current = value;
  }, [value]);

  const handleClick = () => {
    if (onClick) return onClick();
    if (to) return navigate(to);
  };

  const Wrapper = clickable ? "button" : "div";

  return (
    <Wrapper
      type={clickable ? "button" : undefined}
      onClick={clickable ? handleClick : undefined}
      className={[
        "w-full rounded-2xl border bg-mnla-card p-3.5 text-left shadow-[0_1px_2px_rgba(15,23,42,0.035)] sm:p-4",
        "border-mnla-border dark:border-mnla-darkBorder dark:bg-mnla-darkCard",
        clickable ? "cursor-pointer transition hover:border-neutral-300 hover:shadow-sm dark:hover:border-neutral-700" : "",
        clickable
          ? "focus:outline-none focus-visible:ring-2 focus-visible:ring-mnla-accent focus-visible:ring-offset-2 focus-visible:ring-offset-transparent"
          : "",
        accent && clickable ? "hover:border-mnla-accent/60" : "",
        bump ? "ring-1 ring-mnla-accent/35" : "",
      ].join(" ")}
      aria-label={clickable ? `${title}: ${value}` : undefined}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-xs font-semibold uppercase tracking-[0.08em] text-mnla-muted dark:text-mnla-darkMuted">
            {title}
          </div>

          {hint ? (
            <div className="mt-1 text-[11px] text-neutral-500 dark:text-neutral-400 truncate">
              {hint}
            </div>
          ) : null}
        </div>

        {Icon ? (
          <span className="grid h-8 w-8 place-items-center rounded-lg border border-mnla-border bg-neutral-50 text-mnla-text dark:border-mnla-darkBorder dark:bg-neutral-800 dark:text-mnla-darkText">
            <Icon size={16} />
          </span>
        ) : null}
      </div>

      <div
        className={[
          "mt-3 text-xl font-semibold tracking-[-0.025em] text-mnla-text dark:text-mnla-darkText tabular-nums",
          "transition-transform duration-200",
          bump ? "translate-y-[-1px] scale-[1.01]" : "translate-y-0 scale-100",
        ].join(" ")}
      >
        {value}
      </div>

      {clickable ? (
        <div className="mt-1.5 text-[11px] font-medium text-neutral-500 dark:text-neutral-400">
          Tap to view
        </div>
      ) : null}
    </Wrapper>
  );
}
